/**
 * Partial view settings stored as a JSON string in the PartialView `Config` field, e.g.
 * `{"DisplayStatus":true,"LabelTextKey":"AccountInfo.Header"}`.
 * LabelTextKey is a translation key in the `WidgetConfiguration.PartialViews` resource group.
 */
export interface PartialViewConfigSettings {
  /** 1 = Active, 0 = Inactive. Defaults to Active when absent. */
  displayStatus: number;
  labelTextKey: string;
  /** Language the label text is saved as a translation for (only sent on save). */
  languageCode?: string;
}

/** Name comparison key for partial views: "Account Info", "AccountInfo" and "account_info" all match. */
export function normalizePartialViewName(value: string | null | undefined): string {
  return (value ?? '').replace(/[\s_-]+/g, '').toLowerCase();
}

function readCaseInsensitive(raw: Record<string, unknown>, name: string): unknown {
  const key = Object.keys(raw).find((k) => k.toLowerCase() === name.toLowerCase());
  return key ? raw[key] : undefined;
}

function parseConfigObject(config: string | null | undefined): Record<string, unknown> {
  if (!config || !config.trim()) return {};
  try {
    const parsed = JSON.parse(config);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** Read the settings from a saved Config string (tolerates null, invalid JSON and any key casing). */
export function parsePartialViewConfig(config: string | null | undefined): PartialViewConfigSettings {
  const raw = parseConfigObject(config);
  const status = readCaseInsensitive(raw, 'DisplayStatus');
  const label = readCaseInsensitive(raw, 'LabelTextKey');
  // Stored as a boolean (true = Active); numbers / "true" / "false" strings from older data are tolerated.
  const inactive = status === false || status === 0 || status === '0' || String(status).toLowerCase() === 'false';
  return {
    displayStatus: inactive ? 0 : 1,
    labelTextKey: typeof label === 'string' ? label : '',
  };
}

/** Build the Config object (sent as `{ Config: {...} }`), keeping any other keys already stored. */
export function buildPartialViewConfig(
  existingConfig: string | null | undefined,
  settings: PartialViewConfigSettings
): Record<string, unknown> {
  const raw = parseConfigObject(existingConfig);
  for (const name of ['DisplayStatus', 'LabelTextKey', 'LanguageCode']) {
    const key = Object.keys(raw).find((k) => k.toLowerCase() === name.toLowerCase());
    if (key) delete raw[key];
  }
  return {
    ...raw,
    DisplayStatus: Number(settings.displayStatus) !== 0,
    LabelTextKey: (settings.labelTextKey ?? '').trim(),
    ...(settings.languageCode ? { LanguageCode: settings.languageCode.trim().toLowerCase() } : {}),
  };
}
